import React, { useEffect, useCallback } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string | string[];
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  keywords = [],
  image,
  url,
  type = 'article',
  publishedTime,
  modifiedTime,
  author = '古代生物図鑑 Siska',
}) => {
  const siteName = '古代生物図鑑 Siska';
  // process.env.PUBLIC_URLがundefinedの場合のフォールバック処理
  const publicUrl = process.env.PUBLIC_URL || '';
  const defaultImage = publicUrl
    ? `${publicUrl}/images/article-placeholder.svg`
    : '/images/article-placeholder.svg';
  const ogImage = image || defaultImage;
  const canonicalUrl = url || window.location.href;

  // 既存のメタタグを削除する関数
  const removeExistingMeta = (selector: string) => {
    const existing = document.querySelector(selector);
    if (existing) {
      existing.remove();
    }
  };

  // 基本メタタグの設定
  const setupBasicMetaTags = useCallback(
    (description: string, normalizedKeywords: string[], canonicalUrl: string) => {
      // 既存のメタタグを削除
      removeExistingMeta('meta[name="description"]');
      removeExistingMeta('meta[name="keywords"]');
      removeExistingMeta('link[rel="canonical"]');

      // 説明メタタグ
      const descriptionMeta = document.createElement('meta');
      descriptionMeta.name = 'description';
      descriptionMeta.content = description;
      document.head.appendChild(descriptionMeta);

      // キーワードメタタグ
      if (normalizedKeywords.length > 0) {
        const keywordsMeta = document.createElement('meta');
        keywordsMeta.name = 'keywords';
        keywordsMeta.content = normalizedKeywords.join(', ');
        document.head.appendChild(keywordsMeta);
      }

      // 正規化リンク
      const canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      canonicalLink.href = canonicalUrl;
      document.head.appendChild(canonicalLink);
    },
    []
  );

  // OGPタグの設定
  const setupOGPTags = (
    title: string,
    description: string,
    type: string,
    canonicalUrl: string,
    ogImage: string,
    siteName: string
  ) => {
    const ogTags = [
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:type', content: type },
      { property: 'og:url', content: canonicalUrl },
      { property: 'og:image', content: ogImage },
      { property: 'og:image:alt', content: title },
      { property: 'og:site_name', content: siteName },
      { property: 'og:locale', content: 'ja_JP' },
      { property: 'fb:app_id', content: process.env.REACT_APP_FACEBOOK_APP_ID || '' },
    ];

    // 既存のOGPタグを削除
    document.querySelectorAll('meta[property^="og:"]').forEach(meta => meta.remove());

    // OGPタグを追加
    ogTags.forEach(({ property, content }) => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', property);
      meta.content = content;
      document.head.appendChild(meta);
    });
  };

  // Twitter Cardの設定
  const setupTwitterTags = (title: string, description: string, ogImage: string) => {
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:site', content: process.env.REACT_APP_TWITTER_HANDLE || '' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: ogImage },
      { name: 'twitter:image:alt', content: title },
    ];

    // 既存のTwitterタグを削除
    document.querySelectorAll('meta[name^="twitter:"]').forEach(meta => meta.remove());

    // Twitterタグを追加
    twitterTags.forEach(({ name, content }) => {
      const meta = document.createElement('meta');
      meta.name = name;
      meta.content = content;
      document.head.appendChild(meta);
    });
  };

  // 記事固有のメタタグの設定
  const setupArticleTags = (
    author: string,
    normalizedKeywords: string[],
    publishedTime?: string,
    modifiedTime?: string
  ) => {
    const articleTags = [
      { property: 'article:author', content: author },
      { property: 'article:section', content: '古生物学' },
    ];

    if (publishedTime) {
      articleTags.push({ property: 'article:published_time', content: publishedTime });
    }
    if (modifiedTime) {
      articleTags.push({ property: 'article:modified_time', content: modifiedTime });
    }

    // 既存の記事タグを削除
    document.querySelectorAll('meta[property^="article:"]').forEach(meta => meta.remove());

    // 記事タグを追加
    articleTags.forEach(({ property, content }) => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', property);
      meta.content = content;
      document.head.appendChild(meta);
    });

    // キーワードタグ
    normalizedKeywords.forEach(keyword => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'article:tag');
      meta.content = keyword;
      document.head.appendChild(meta);
    });

    // Google News用のメタタグ
    const newsKeywords = ['古生物学', '恐竜', '化石', ...normalizedKeywords].join(', ');
    const newsKeywordsMeta = document.createElement('meta');
    newsKeywordsMeta.name = 'news_keywords';
    newsKeywordsMeta.content = newsKeywords;
    document.head.appendChild(newsKeywordsMeta);

    // 記事の言語設定
    const languageMeta = document.createElement('meta');
    languageMeta.name = 'language';
    languageMeta.content = 'ja';
    document.head.appendChild(languageMeta);

    // 地理的ターゲティング
    const geoRegionMeta = document.createElement('meta');
    geoRegionMeta.name = 'geo.region';
    geoRegionMeta.content = 'JP';
    document.head.appendChild(geoRegionMeta);

    // コンテンツの新鮮度を示す
    if (publishedTime) {
      const isRecent = new Date(publishedTime) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000); // 7日以内
      if (isRecent) {
        const freshnessMeta = document.createElement('meta');
        freshnessMeta.name = 'article:freshness_date';
        freshnessMeta.content = publishedTime;
        document.head.appendChild(freshnessMeta);
      }
    }
  };

  // 構造化データの設定
  const setupStructuredData = useCallback(
    (
      title: string,
      description: string,
      ogImage: string,
      canonicalUrl: string,
      type: string,
      author: string,
      siteName: string,
      normalizedKeywords: string[],
      publishedTime?: string,
      modifiedTime?: string
    ) => {
      const existingJsonLd = document.querySelector('script[type="application/ld+json"]');
      if (existingJsonLd) {
        existingJsonLd.remove();
      }

      const jsonLdScript = document.createElement('script');
      jsonLdScript.type = 'application/ld+json';

      const structuredData =
        type === 'article'
          ? createArticleStructuredData(
              title,
              description,
              ogImage,
              canonicalUrl,
              author,
              siteName,
              normalizedKeywords,
              publishedTime,
              modifiedTime
            )
          : createPageStructuredData(title, description, canonicalUrl, ogImage);

      jsonLdScript.textContent = JSON.stringify(structuredData);
      document.head.appendChild(jsonLdScript);
    },
    []
  );

  // 記事の構造化データ作成
  const createArticleStructuredData = (
    title: string,
    description: string,
    ogImage: string,
    canonicalUrl: string,
    author: string,
    siteName: string,
    normalizedKeywords: string[],
    publishedTime?: string,
    modifiedTime?: string
  ) => ({
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: title,
    description: description,
    image: {
      '@type': 'ImageObject',
      url: ogImage,
      width: 1200,
      height: 630,
    },
    url: canonicalUrl,
    datePublished: publishedTime,
    dateModified: modifiedTime || publishedTime,
    author: {
      '@type': 'Organization',
      name: author,
      url: window.location.origin,
    },
    publisher: {
      '@type': 'Organization',
      name: siteName,
      url: window.location.origin,
      logo: {
        '@type': 'ImageObject',
        url: `${window.location.origin}/logo192.png`,
        width: 192,
        height: 192,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
    articleSection: '古生物学',
    keywords: normalizedKeywords.join(', '),
    inLanguage: 'ja-JP',
    isAccessibleForFree: true,
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['h1', '.article-summary'],
    },
  });

  // ページの構造化データ作成
  const createPageStructuredData = (
    title: string,
    description: string,
    canonicalUrl: string,
    ogImage: string
  ) => ({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description: description,
    url: canonicalUrl,
    image: ogImage,
    inLanguage: 'ja-JP',
  });

  // メタタグのクリーンアップ
  const cleanupMetaTags = () => {
    document.querySelectorAll('meta[property^="og:"]').forEach(meta => meta.remove());
    document.querySelectorAll('meta[name^="twitter:"]').forEach(meta => meta.remove());
    document.querySelectorAll('meta[property^="article:"]').forEach(meta => meta.remove());
    document
      .querySelectorAll('script[type="application/ld+json"]')
      .forEach(script => script.remove());
  };

  useEffect(() => {
    // キーワードを配列に正規化（useEffect内で実行）
    const normalizedKeywords = Array.isArray(keywords)
      ? keywords
      : typeof keywords === 'string'
        ? keywords
            .split(',')
            .map(kw => kw.trim())
            .filter(Boolean)
        : [];

    // ページタイトルを設定
    document.title = `${title} - ${siteName}`;

    // 基本メタタグを設定
    setupBasicMetaTags(description, normalizedKeywords, canonicalUrl);

    // OGP メタタグを設定
    setupOGPTags(title, description, type, canonicalUrl, ogImage, siteName);

    // Twitter Cardを設定
    setupTwitterTags(title, description, ogImage);

    // 記事固有のメタタグを設定
    if (type === 'article') {
      setupArticleTags(author, normalizedKeywords, publishedTime, modifiedTime);
    }

    // 構造化データを設定
    setupStructuredData(
      title,
      description,
      ogImage,
      canonicalUrl,
      type,
      author,
      siteName,
      normalizedKeywords,
      publishedTime,
      modifiedTime
    );

    // クリーンアップ関数
    return () => {
      cleanupMetaTags();
    };
  }, [
    title,
    description,
    keywords,
    ogImage,
    canonicalUrl,
    type,
    publishedTime,
    modifiedTime,
    author,
    siteName,
    setupBasicMetaTags,
    setupStructuredData,
  ]);

  return null;
};

export default SEOHead;
